import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { Inbox, Plus, Send, Zap, CheckCircle2, Trash2, Play, UserCheck, Link2, ExternalLink } from "lucide-react";
import { fDateTime } from "@/lib/format";
import { toast } from "sonner";

const BT = [
  { v: "routine", tk: "bt_routine" }, { v: "policy", tk: "bt_policy" }, { v: "survey", tk: "bt_survey" },
  { v: "celebration", tk: "bt_celebration" }, { v: "training", tk: "bt_training" }, { v: "other", tk: "bt_other" },
];
const AUD = [
  { v: "all", tk: "aud_all" }, { v: "business_area", tk: "aud_area" }, { v: "department", tk: "aud_dept" },
  { v: "team", tk: "aud_team" }, { v: "shift", tk: "aud_shift" }, { v: "individual", tk: "aud_individual" },
];
const ST = { open: "up_status_open", in_progress: "up_status_in_progress", published: "up_status_published", archived: "up_status_archived" };

function CreateBrief({ refs, assignees, canSeePeople, onCreated }) {
  const { t } = useLang();
  const init = { title: "", description: "", brief_type: "routine", asap: false, audienceType: "all", audienceId: "", assignee_id: "none" };
  const [f, setF] = useState(init);
  const AUD_OPTS = AUD.filter((o) => o.v !== "individual" || canSeePeople);
  const opts = f.audienceType === "business_area" ? refs.businessAreas : f.audienceType === "department" ? refs.departments
    : f.audienceType === "team" ? refs.teams : f.audienceType === "shift" ? refs.shifts
    : f.audienceType === "individual" ? refs.employees : [];
  const optName = (o) => o.name || `${o.first_name || ""} ${o.last_name || ""}`.trim();
  const send = async () => {
    if (!f.title.trim()) return toast.error(t("enter_title"));
    const audience = f.audienceType === "all" ? { type: "all" } : { type: f.audienceType, ids: f.audienceId ? [f.audienceId] : [] };
    try {
      await api.post("/briefs", { title: f.title, description: f.description, brief_type: f.brief_type, asap: f.asap, audience, assignee_id: f.assignee_id === "none" ? null : f.assignee_id });
      toast.success(t("up_created"));
      setF(init);
      onCreated();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mb-6 grid gap-3 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label>{t("title_lbl")}</Label><Input value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} data-testid="brief-title" /></div>
        <div><Label>{t("type_lbl")}</Label>
          <Select value={f.brief_type} onValueChange={(v) => setF((s) => ({ ...s, brief_type: v }))}>
            <SelectTrigger data-testid="brief-type"><SelectValue /></SelectTrigger>
            <SelectContent>{BT.map((b) => <SelectItem key={b.v} value={b.v}>{t(b.tk)}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div><Label>{t("desc_lbl")}</Label><Textarea rows={2} value={f.description} onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} data-testid="brief-desc" /></div>
      <div className="flex flex-wrap items-end gap-3">
        <div><Label>{t("sv_audience")}</Label>
          <Select value={f.audienceType} onValueChange={(v) => setF((s) => ({ ...s, audienceType: v, audienceId: "" }))}>
            <SelectTrigger className="w-44" data-testid="brief-aud-type"><SelectValue /></SelectTrigger>
            <SelectContent>{AUD_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{t(o.tk)}</SelectItem>)}</SelectContent>
          </Select></div>
        {f.audienceType !== "all" && (
          <Select value={f.audienceId} onValueChange={(v) => setF((s) => ({ ...s, audienceId: v }))}>
            <SelectTrigger className="w-52" data-testid="brief-aud-target"><SelectValue placeholder={t("choose")} /></SelectTrigger>
            <SelectContent>{(opts || []).map((o) => <SelectItem key={o.id} value={o.id}>{optName(o)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div><Label>{t("up_assignee")}</Label>
          <Select value={f.assignee_id} onValueChange={(v) => setF((s) => ({ ...s, assignee_id: v }))}>
            <SelectTrigger className="w-52" data-testid="brief-assignee-new"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("up_assignee_none")}</SelectItem>
              {(assignees || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <label className="flex items-center gap-2 text-sm" data-testid="brief-asap">
          <Checkbox checked={f.asap} onCheckedChange={(v) => setF((s) => ({ ...s, asap: !!v }))} /> <Zap className="h-4 w-4 text-amber-600" /> {t("up_asap")}
        </label>
        <Button className="ml-auto" onClick={send} data-testid="brief-send"><Send className="mr-1 h-4 w-4" />{t("up_send")}</Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("up_default_admin")}</p>
    </Card>
  );
}

function BriefCard({ b, canManage, mine, assignees, posts, onChanged }) {
  const { t } = useLang();
  const [note, setNote] = useState(b.note || "");
  const [assignee, setAssignee] = useState(b.assignee_id || "none");
  const [linkPost, setLinkPost] = useState(b.feed_id || "none");
  const bt = BT.find((x) => x.v === b.brief_type);
  const canAssign = canManage || mine;
  const patch = async (payload, ok) => { try { await api.patch(`/briefs/${b.id}`, payload); toast.success(ok || t("up_updated")); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async () => { if (!window.confirm(t("up_confirm_delete"))) return; try { await api.delete(`/briefs/${b.id}`); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const postLabel = (p) => p.title || (p.body || "").slice(0, 40) || p.id;
  return (
    <Card className="p-4" data-testid={`brief-${b.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {b.asap && <Badge variant="destructive" className="gap-1"><Zap className="h-3 w-3" />ASAP</Badge>}
        <span className="font-medium">{b.title}</span>
        <Badge variant="outline">{bt ? t(bt.tk) : b.brief_type}</Badge>
        <Badge variant={b.status === "published" ? "default" : b.status === "in_progress" ? "secondary" : "outline"}>{t(ST[b.status] || "up_status_open")}</Badge>
        <Badge variant={b.assignee_name ? "secondary" : "outline"} className="gap-1 text-[10px]">
          <UserCheck className="h-3 w-3" />{b.assignee_name || t("up_assignee_none")}
        </Badge>
        <span className="ml-auto text-xs text-muted-foreground">{t("up_by", { name: b.created_by_name })} · {fDateTime(b.created_at)}</span>
      </div>
      {b.description && <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{b.description}</p>}
      {b.note && <p className="mt-1 text-xs text-accent">{b.note}</p>}
      {b.feed_id && (
        <Link to={`/flode?post=${b.feed_id}`} className="mt-2 inline-flex items-center gap-1 text-sm text-accent underline" data-testid={`brief-view-${b.id}`}>
          <ExternalLink className="h-4 w-4" />{t("up_view_content")}
        </Link>
      )}
      {canAssign && b.status !== "archived" && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">{t("up_assignee")}</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger className="w-52" data-testid={`brief-assignee-${b.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("up_assignee_none")}</SelectItem>
                {(assignees || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <Button size="sm" variant="outline" onClick={() => patch({ assignee_id: assignee === "none" ? "" : assignee })} data-testid={`brief-assignee-save-${b.id}`}>{t("up_assign_save")}</Button>
        </div>
      )}
      {canManage && b.status !== "archived" && (
        <div className="mt-2 space-y-2">
          <div className="flex gap-2">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("up_note_ph")} data-testid={`brief-note-${b.id}`} />
            <Button size="sm" variant="outline" onClick={() => patch({ note })} data-testid={`brief-note-save-${b.id}`}>{t("up_save_note")}</Button>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">{t("up_link_feed")}</Label>
              <Select value={linkPost} onValueChange={setLinkPost}>
                <SelectTrigger className="w-60" data-testid={`brief-link-${b.id}`}><SelectValue placeholder={t("up_choose_post")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {(posts || []).map((p) => <SelectItem key={p.id} value={p.id}>{postLabel(p)}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <Button size="sm" variant="outline" onClick={() => patch({ feed_id: linkPost === "none" ? "" : linkPost })} data-testid={`brief-link-save-${b.id}`}><Link2 className="mr-1 h-4 w-4" />{t("save")}</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {b.status === "open" && <Button size="sm" variant="outline" onClick={() => patch({ status: "in_progress" })} data-testid={`brief-take-${b.id}`}><Play className="mr-1 h-4 w-4" />{t("up_take")}</Button>}
            {b.status !== "published" && <Button size="sm" onClick={() => patch({ status: "published", note, feed_id: linkPost === "none" ? "" : linkPost })} data-testid={`brief-publish-${b.id}`}><CheckCircle2 className="mr-1 h-4 w-4" />{t("up_mark_published")}</Button>}
            <Button size="sm" variant="ghost" onClick={() => patch({ status: "archived" })} data-testid={`brief-archive-${b.id}`}>{t("up_archive")}</Button>
          </div>
        </div>
      )}
      {(canManage || mine) && <div className="mt-2"><Button size="sm" variant="ghost" onClick={del} data-testid={`brief-del-${b.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>}
    </Card>
  );
}

export default function Uppdrag() {
  const { t } = useLang();
  const { user, effectiveRole } = useAuth();
  const canCreate = ["owner", "hr_admin", "chef", "teamledare"].includes(effectiveRole);
  const canManage = ["owner", "creator"].includes(effectiveRole);
  const canSeePeople = (user?.access_level ?? 1) >= 3;
  const { data: briefs, reload } = useApi("/briefs");
  const { data: assignees } = useApi("/briefs/assignees");
  const { data: feedData } = useApi("/feed");
  const { data: departments } = useApi("/departments");
  const { data: teams } = useApi("/teams");
  const { data: shifts } = useApi("/shifts");
  const { data: businessAreas } = useApi("/business_areas");
  const { data: employees } = useApi(canSeePeople ? "/employees" : null);
  const refs = { departments, teams, shifts, businessAreas, employees };
  const posts = feedData?.posts || [];

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={t("up_title")} icon={Inbox} subtitle={canManage ? t("up_sub_creator") : t("up_sub_manage")} />
      {canCreate && <CreateBrief refs={refs} assignees={assignees} canSeePeople={canSeePeople} onCreated={reload} />}
      {(briefs || []).length === 0 ? (
        <EmptyState icon={Inbox} title={t("up_empty_title")} text={canManage ? t("up_empty_creator") : t("up_empty_manage")} />
      ) : (
        <div className="space-y-3">
          {briefs.map((b) => <BriefCard key={b.id} b={b} canManage={canManage} mine={b.created_by === user?.email} assignees={assignees} posts={posts} onChanged={reload} />)}
        </div>
      )}
    </div>
  );
}
